import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Plus, X, Save, User, Building, Eye, DollarSign, Clock, FileText } from 'lucide-react';

const MembershipTypesTab = ({ membershipTypes, setMembershipTypes, saving, onSave }) => {
  const { t } = useTranslation();

  const updateType = (index, field, value) => {
    const updated = [...membershipTypes];
    if (typeof field === 'object') {
      // Support updating multiple fields at once: updateType(index, {field1: val1, field2: val2})
      updated[index] = { ...updated[index], ...field };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setMembershipTypes(updated);
  };

  const addBullet = (typeIndex) => {
    const updated = [...membershipTypes];
    updated[typeIndex] = {
      ...updated[typeIndex],
      description_bullets: [...(updated[typeIndex].description_bullets || []), '']
    };
    setMembershipTypes(updated);
  };

  const updateBullet = (typeIndex, bulletIndex, value) => {
    const updated = [...membershipTypes];
    const bullets = [...updated[typeIndex].description_bullets];
    bullets[bulletIndex] = value;
    updated[typeIndex] = { ...updated[typeIndex], description_bullets: bullets };
    setMembershipTypes(updated);
  };

  const removeBullet = (typeIndex, bulletIndex) => {
    const updated = [...membershipTypes];
    const bullets = updated[typeIndex].description_bullets.filter((_, i) => i !== bulletIndex);
    updated[typeIndex] = { ...updated[typeIndex], description_bullets: bullets };
    setMembershipTypes(updated);
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'building': return <Building className="w-5 h-5" />;
      case 'eye': return <Eye className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const durationPresets = [
    { label: '7 Days', value: 7 },
    { label: '1 Month', value: 30 },
    { label: '3 Months', value: 90 },
    { label: '6 Months', value: 180 },
    { label: '1 Year', value: 365 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{t('admin.membershipTypes', 'Membership Types')}</h2>
          <p className="text-zinc-400 text-sm mt-1">{t('admin.membershipTypesDesc', 'Edit membership types shown on the user registration screen')}</p>
        </div>
        <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </Button>
      </div>

      <div className="space-y-4">
        {membershipTypes.map((type, typeIndex) => (
          <div key={type.id} className="bg-zinc-800/50 rounded-xl border border-zinc-700 p-5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center text-zinc-300">
                  {getIcon(type.icon)}
                </div>
                <div className="flex-1">
                  <Label className="text-zinc-400 text-xs">{t('admin.membershipTitle', 'Title')}</Label>
                  <Input
                    value={type.title}
                    onChange={(e) => updateType(typeIndex, 'title', e.target.value)}
                    className="bg-zinc-900 border-zinc-600 text-white mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Label className="text-zinc-400 text-xs">{t('common.active', 'Active')}</Label>
                <Switch
                  checked={type.active}
                  onCheckedChange={(checked) => updateType(typeIndex, 'active', checked)}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mb-4 p-4 rounded-lg bg-zinc-900/50 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-emerald-400" />
                <Label className="text-zinc-300 text-sm font-medium">{t('admin.pricingSettings', 'Pricing Settings')}</Label>
              </div>

              {/* Free/Paid Toggle */}
              <div className="flex items-center gap-3 mb-4">
                <Label className="text-zinc-400 text-xs">{t('admin.isFree', 'Free Plan')}</Label>
                <Switch
                  checked={type.is_free !== false}
                  onCheckedChange={(checked) => {
                    updateType(typeIndex, checked ? { is_free: true, price: 0 } : { is_free: false });
                  }}
                  className="data-[state=checked]:bg-emerald-500"
                />
                <span className={`text-xs px-2 py-0.5 rounded-full ${type.is_free !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {type.is_free !== false ? t('admin.free', 'Free') : t('admin.paid', 'Paid')}
                </span>
              </div>

              {/* Price (only for paid) */}
              {type.is_free === false && (
                <div className="flex items-end gap-3 mb-4">
                  <div className="flex-1">
                    <Label className="text-zinc-400 text-xs mb-2 block">{t('admin.monthlyPrice', 'Monthly Price')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={type.price || 0}
                        onChange={(e) => updateType(typeIndex, 'price', parseFloat(e.target.value) || 0)}
                        className="bg-zinc-900 border-zinc-600 text-white w-32 h-9"
                      />
                      <select
                        value={type.currency || '₺'}
                        onChange={(e) => updateType(typeIndex, 'currency', e.target.value)}
                        className="bg-zinc-900 border border-zinc-600 text-white rounded-md h-9 px-2 text-sm"
                      >
                        <option value="₺">₺ TL</option>
                        <option value="$">$ USD</option>
                        <option value="€">€ EUR</option>
                        <option value="£">£ GBP</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Duration */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3.5 h-3.5 text-zinc-400" />
                  <Label className="text-zinc-400 text-xs">{t('admin.duration', 'Membership Duration (Days)')}</Label>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {durationPresets.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => updateType(typeIndex, 'duration_days', preset.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        type.duration_days === preset.value
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                          : 'bg-zinc-900 text-zinc-400 border border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                  <Input
                    type="number"
                    min="1"
                    value={type.duration_days || 30}
                    onChange={(e) => updateType(typeIndex, 'duration_days', parseInt(e.target.value) || 30)}
                    className="bg-zinc-900 border-zinc-600 text-white w-20 h-8 text-xs"
                    placeholder="Days"
                  />
                </div>
              </div>

              {/* Note */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-3.5 h-3.5 text-zinc-400" />
                  <Label className="text-zinc-400 text-xs">{t('admin.membershipNote', 'Note Shown During Registration')}</Label>
                </div>
                <Input
                  value={type.note || ''}
                  onChange={(e) => updateType(typeIndex, 'note', e.target.value)}
                  className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm"
                  placeholder={type.is_free !== false ? 'E.g.: 30 days free trial' : 'E.g.: Monthly automatic payment'}
                />
              </div>
            </div>

            {/* Max Listings */}
            <div className="mb-4">
              <Label className="text-zinc-400 text-xs mb-2 block">{t('admin.maxListings', 'Maximum Listing Count')}</Label>
              <Input
                type="number"
                min="1"
                max="999"
                value={type.max_listings || 1}
                onChange={(e) => updateType(typeIndex, 'max_listings', parseInt(e.target.value) || 1)}
                className="bg-zinc-900 border-zinc-600 text-white w-32 h-9"
              />
            </div>

            {/* Icon Selection */}
            <div className="mb-4">
              <Label className="text-zinc-400 text-xs mb-2 block">{t('admin.membershipIcon', 'Icon')}</Label>
              <div className="flex gap-2">
                {['user', 'building', 'eye'].map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => updateType(typeIndex, 'icon', icon)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      type.icon === icon
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                        : 'bg-zinc-900 text-zinc-400 border border-zinc-700 hover:border-zinc-500'
                    }`}
                  >
                    {getIcon(icon)}
                  </button>
                ))}
              </div>
            </div>

            {/* Bullet Points */}
            <div>
              <Label className="text-zinc-400 text-xs mb-2 block">{t('admin.membershipBullets', 'Description Bullets')}</Label>
              <div className="space-y-2">
                {(type.description_bullets || []).map((bullet, bulletIndex) => (
                  <div key={bulletIndex} className="flex items-center gap-2">
                    <span className="text-emerald-500 text-sm">•</span>
                    <Input
                      value={bullet}
                      onChange={(e) => updateBullet(typeIndex, bulletIndex, e.target.value)}
                      className="bg-zinc-900 border-zinc-600 text-white flex-1 h-9 text-sm"
                      placeholder={t('admin.membershipBulletPlaceholder', 'Bullet description...')}
                    />
                    <button
                      type="button"
                      onClick={() => removeBullet(typeIndex, bulletIndex)}
                      className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addBullet(typeIndex)}
                  className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('admin.addBullet', 'Add Bullet')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MembershipTypesTab;
